import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import arenaTicketOpening from '@sorare/core/src/assets/rivals/arena-ticket-opening.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayL, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from 'components/rivals/GridBackground';
import { BackgroundAligned } from 'components/rivals/GridBackground/BackgroundAligned';

import type { ArenaTicketOpeningOnboardingDialog_probabilisticBundleArenaTicketSlotItem } from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  padding: var(--quadruple-unit) var(--double-unit);
  @media ${tabletAndAbove} {
    height: min(100vh - 8 * var(--unit), 812px);
  }
`;

const Content = styled(Vertical).attrs({ gap: 4 })`
  isolation: isolate;
  position: absolute;
  bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    position: static;
    height: 30%;
    justify-content: flex-end;
  }
`;
const StyledStarBall = styled(StarBall)`
  margin-top: -40px;
`;
const StyledBackgroundAligned = styled(BackgroundAligned)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit);
`;
const Badge = styled(Horizontal).attrs({ gap: 0, center: true })`
  border-radius: var(--double-unit);
  width: min-content;
  padding: 2px var(--unit);
  border: 1px solid var(--c-rewards-pink);
  background: linear-gradient(
      -25deg,
      rgba(255, 9, 116, 0.15) 50%,
      transparent 50%
    )
    padding-box;
  align-self: center;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  arenaTicketSlotItem: ArenaTicketOpeningOnboardingDialog_probabilisticBundleArenaTicketSlotItem;
};

export const ArenaTicketOpeningOnboardingDialog = ({
  open,
  onClose,
  arenaTicketSlotItem,
}: Props) => {
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs">
      <GridBackground height="70%" bgColor="var(--c-nd-50-opaque)">
        <StyledBackgroundAligned
          horizontalDelta={0}
          verticalDelta={-0.23}
          maxWidth={2}
        >
          <img src={arenaTicketOpening} alt="" width={160} />
          <Badge>
            <LabelL
              bold
              color="var(--c-rewards-pink)"
            >{`x${arenaTicketSlotItem.quantity}`}</LabelL>
          </Badge>
        </StyledBackgroundAligned>
      </GridBackground>
      <Content gap={4}>
        <Vertical>
          <LabelM>
            <FormattedMessage
              id="AreaTicketOpeningOnboardingDialog.subtitle"
              defaultMessage="You won"
            />
          </LabelM>
          <DisplayL color="var(--c-rewards-pink)">
            <FormattedMessage
              id="AreaTicketOpeningOnboardingDialog.title"
              defaultMessage="Arena Tickets"
            />
            <StyledStarBall color="var(--c-rewards-pink" width={16} />
          </DisplayL>
          <LabelM>
            <FormattedMessage
              id="AreaTicketOpeningOnboardingDialog.description"
              defaultMessage="You can use these tickets to enter the Arena in Rivals to play 1v1 matches against managers."
            />
          </LabelM>
        </Vertical>
        <Button size="large" color="secondary" onClick={onClose}>
          <FormattedMessage
            id="AreaTicketOpeningOnboardingDialog.cta"
            defaultMessage="Got it"
          />
        </Button>
      </Content>
    </StyledDialog>
  );
};

ArenaTicketOpeningOnboardingDialog.fragments = {
  arenaTicketSlotItem: gql`
    fragment ArenaTicketOpeningOnboardingDialog_probabilisticBundleArenaTicketSlotItem on ProbabilisticBundleSlotArenaTicketItem {
      id
      quantity
    }
  ` as TypedDocumentNode<ArenaTicketOpeningOnboardingDialog_probabilisticBundleArenaTicketSlotItem>,
};
